import React, { FC, useEffect, useMemo, useState } from 'react';
import useTranslations from '@hooks/useTranslations';
import Collapsible from 'react-collapsible';
import Icon, { Icons } from '@components/Icon';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useMoneyFormatter } from '@hooks/useMoneyFormatter';
import { AccessibleButton } from '@components/Button/AccessibleButton';
import { BasketOptionalFeesDetailsProps } from '@components/v2/BasketOptionalPartsDetails/index';
import Checkbox from '@components/Checkbox';
import { IDealerFee } from '@components/DealerFees/DealerFeesTemplate';
import UIDuck from '../../../redux/commonDucks/ui.duck';
import DealService from '../../../services/deal/deal.service';
import { getSessionIdCookie } from '@utils/Session.utils';
import CarDetailsDuck from '../../../redux/carDetails/carDetails.duck';
import {
    BASKET_STEPS,
    CARTE_GRISE_FEE,
    isBrandAC,
    MALUS_FEE,
    ModalVersion,
    REGISTRATION_FEE,
    REGISTRATION_FEES_VALUES,
} from '../../../constants/main';
import { theme } from '../../../styles/theme';
import Spinner from '@components/Spinner';
import isEmpty from 'lodash/isEmpty';
import Button from '@components/Button';
import { PERSONALIZE_OFFER_SECTIONS } from '@components/Modal/ModalPersonalizeOffer/ModalPersonalizeOfferTemplate';
import { IconTemplate } from '@components/Icon/IconTemplate';
import { useUserDuck } from '@hooks/useUserDuck';
import { useGTM } from '@hooks/useGTM';

const MIN_COUNT_OF_CHARACTERS_TO_SHOW_DETAIL_BUTTON = 150;

const BasketOptionalFeesTemplate: FC<BasketOptionalFeesDetailsProps> = ({ className, generatedIndex }) => {
    const { t } = useTranslations();
    const dispatch = useDispatch();
    const { pushToDataLayer } = useGTM();
    const { currentDeal } = useCarDetailsDuck();
    const { isUserLoggedInAsDealer } = useUserDuck();
    const { formatMoney } = useMoneyFormatter();
    const isLoading = useSelector((state) => !!UIDuck.getOwnState(state).loading);

    const [isOpen, setIsOpen] = useState(true);
    const [openedLabelIds, setOpenedLabelIds] = useState([]);

    const [selectedOptionalDealerFees, setSelectedOptionalDealerFees] = useState<IDealerFee[]>(
        currentDeal?.fullProductConfiguration?.fees || []
    );

    const dealerFees = useMemo(
        () => (!isEmpty(currentDeal?.extraFields?.dealerFees) ? JSON.parse(currentDeal?.extraFields?.dealerFees) : []),
        [currentDeal?.extraFields?.dealerFees]
    );

    useEffect(() => {
        setSelectedOptionalDealerFees(currentDeal?.fullProductConfiguration?.fees || []);
    }, [currentDeal?.fullProductConfiguration?.fees]);

    const filteredOptionalDealerFees = dealerFees.filter(
        (dealerFee: IDealerFee) =>
            !dealerFee.mandatory &&
            dealerFee.id !== CARTE_GRISE_FEE &&
            dealerFee.id !== REGISTRATION_FEE &&
            dealerFee.id !== MALUS_FEE
    );

    const handleDescriptionClick = (id: string) => {
        if (openedLabelIds.find((openedId) => openedId === id)) {
            return setOpenedLabelIds(openedLabelIds.filter((openedId) => openedId !== id));
        }

        return setOpenedLabelIds([...openedLabelIds, id]);
    };

    const isOpenedLabelById = (id: string) => openedLabelIds.find((openedId) => id === openedId);

    const handleOptionClick = async (dealerFee: IDealerFee) => {
        dispatch(UIDuck.setLoading(5));
        const isSelected = selectedOptionalDealerFees.find(
            (selectedDealerFee: IDealerFee) => selectedDealerFee.id === dealerFee.id
        );
        const optionalDealerFeesSummary = isSelected
            ? selectedOptionalDealerFees.filter(
                  (selectedDealerFee: IDealerFee) => selectedDealerFee.id !== dealerFee.id
              )
            : [...selectedOptionalDealerFees, dealerFee];
        setSelectedOptionalDealerFees(optionalDealerFeesSummary);

        const selectedFeesChoise = currentDeal?.extraFields?.selectedFeesValue;

        const { data } = await DealService.updateDeal(
            {
                dealerFees: optionalDealerFeesSummary,
                ...(currentDeal?.extraFields?.postalCode && { postalCode: currentDeal?.extraFields?.postalCode }),
                includeFees: selectedFeesChoise === REGISTRATION_FEES_VALUES.YES,
            },
            getSessionIdCookie(),
            true
        );

        batch(() => {
            dispatch(CarDetailsDuck.setCurrentDeal(null));
            dispatch(CarDetailsDuck.setCurrentDeal(data?.deal));
            dispatch(CarDetailsDuck.setCurrentCarDetails(data?.deal?.fullProductConfiguration));
            dispatch(UIDuck.setLoading(-5));
        });
    };
    const getMoney = (price: number = 0) => {
        return Number(price) === 0 ? t('configurator.colors.includedInPrice') : formatMoney(price, true);
    };

    const isFeeEnabledWithSelectedFees = (dealerFee: IDealerFee) => {
        let isEnabled = true;

        selectedOptionalDealerFees.map((selectedDealerFee: IDealerFee) => {
            const notEnabledFees = selectedDealerFee?.notCompatible?.split(',') ?? [];

            if (notEnabledFees.includes(dealerFee.id)) {
                isEnabled = false;
                return;
            }
        });

        return isEnabled;
    };

    return (
        <div id="optionalFees" className={className}>
            <Collapsible
                lazyRender={true}
                open={isOpen}
                trigger={
                    <AccessibleButton onClick={() => setIsOpen(!isOpen)}>
                        <div className="titleWrapper">
                            <div className="titleContainer">
                                <span className="sectionNumber">{generatedIndex}.</span>
                                <span className="title">{`${t('basket.optionalFees.title').toLowerCase()}`}</span>
                            </div>
                            <Icon
                                className={isOpen ? 'isOpen' : 'isClose'}
                                name={isOpen ? Icons.ChevronUp : Icons.ChevronDown}
                            />
                        </div>
                    </AccessibleButton>
                }
            >
                <div className="parts">
                    <p className="parts__description">{t('basket.optionalFees.description')}</p>
                    <div className="parts__title">
                        <span>{t('basket.optionalFees.subTitle')}</span>
                    </div>
                    {isLoading ? (
                        <Spinner className="spinnerWrapper" size={20} border={3} color={theme.colors.black} />
                    ) : (
                        filteredOptionalDealerFees
                            .sort((a: { title: string }, b: { title: string }) =>
                                t(a.title).toLowerCase() > t(b.title).toLowerCase()
                                    ? 1
                                    : t(b.title).toLowerCase() > t(a.title).toLowerCase()
                                    ? -1
                                    : 0
                            )
                            .map(
                                (dealerFee: IDealerFee, key: number) =>
                                    isFeeEnabledWithSelectedFees(dealerFee) && (
                                        <div
                                            className={`part ${
                                                !!selectedOptionalDealerFees.find(
                                                    (selectedDealerFee: IDealerFee) =>
                                                        selectedDealerFee.id === dealerFee.id
                                                )
                                                    ? 'selected'
                                                    : ''
                                            } feesPart`}
                                            key={key}
                                        >
                                            <div className="part__name">
                                                <Checkbox
                                                    className={'checkbox'}
                                                    onChange={() => handleOptionClick(dealerFee)}
                                                    checked={
                                                        !!selectedOptionalDealerFees.find(
                                                            (selectedDealerFee: IDealerFee) =>
                                                                selectedDealerFee.id === dealerFee.id
                                                        )
                                                    }
                                                    disabled
                                                    name={`${dealerFee.id}-part-checkbox`}
                                                    dataTestId={`TESTING_OPTIONAL_FEE_${dealerFee.title?.toUpperCase()}_CHECKBOX`}
                                                >
                                                    <span></span>
                                                </Checkbox>
                                                <span>{t(dealerFee.title)}</span>
                                                <div className="part__priceWrapper">
                                                    <span
                                                        className="part__priceWrapper_price"
                                                        data-tesstid={`TESTING_OPTIONAL_FEE_${dealerFee.title?.toUpperCase()}_PRICE`}
                                                    >
                                                        {getMoney(dealerFee?.priceInclTax)}
                                                    </span>
                                                </div>
                                                <div
                                                    className={`part__descriptionWrapper ${
                                                        dealerFee.label?.length >
                                                        MIN_COUNT_OF_CHARACTERS_TO_SHOW_DETAIL_BUTTON
                                                            ? 'part__descriptionWrapper_min'
                                                            : ''
                                                    } ${
                                                        isOpenedLabelById(dealerFee.id)
                                                            ? 'part__descriptionWrapper_full'
                                                            : ''
                                                    }`}
                                                >
                                                    <p>{t(dealerFee.label)}</p>
                                                </div>
                                                {dealerFee.label?.length >
                                                    MIN_COUNT_OF_CHARACTERS_TO_SHOW_DETAIL_BUTTON && (
                                                    <button
                                                        onClick={() => handleDescriptionClick(dealerFee.id)}
                                                        className="part__descriptionWrapper_link"
                                                    >
                                                        {isOpenedLabelById(dealerFee.id)
                                                            ? t('basket.mandatoryFees.detail.hide')
                                                            : t('basket.mandatoryFees.detail.show')}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )
                            )
                    )}
                </div>
                {isUserLoggedInAsDealer && (
                    <Button
                        onClick={() => {
                            pushToDataLayer({
                                event: 'uaevent',
                                eventCategory: 'Content',
                                eventAction: 'Display::EditConfigurationModal',
                                eventLabel: t('modal.personalizeOffer.edit.button'),
                            });
                            dispatch(
                                UIDuck.openModal({
                                    data: {
                                        openSection: PERSONALIZE_OFFER_SECTIONS.SUPPLEMENTS,
                                    },
                                    modalType: UIDuck.MODAL_TYPES.PERSONALIZE_OFFER,
                                    modalVersion: ModalVersion.v2,
                                    modalProperties: {
                                        withoutPadding: true,
                                    },
                                })
                            );
                        }}
                        secondary
                        hasEditIcon
                        withoutBackground={isBrandAC}
                        fitContentWidth
                        centerMode
                        marginTop={40}
                        marginBottom={20}
                    >
                        <IconTemplate name={Icons.edit} />
                        {t('modal.personalizeOffer.edit.button')}
                    </Button>
                )}
            </Collapsible>
        </div>
    );
};

export default BasketOptionalFeesTemplate;
